import { Alert, Button as HeroButton } from '@heroui/react'
import { Button } from '@radix-ui/themes'
import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { FaCheck, FaCopy, FaEye, FaEyeSlash } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { $fetch, useFetch } from 'utils/fetch'

const HIDDEN_SECRET = '*'.repeat(64)

export default function WebhookSigningSecret() {
  const [hidden, setHidden] = useState(true)
  const [copied, setCopied] = useState(false)
  const [hiddenWebhookSigningSecret, setHiddenWebhookSigningSecret] = useState()

  const { data: webhookSigningSecret, isLoading: isLoadingWebhookSigningSecret, refetch: refetchWebhookSigningSecret } = useFetch('user/getCurrentWebhookSecret')
  const hasActiveWebhook = webhookSigningSecret?.status === 'active'

  const handleRevealClick = () => {
    setHidden(!hidden)
  }

  const handleCopyClick = () => {
    navigator.clipboard
      .writeText(hiddenWebhookSigningSecret)
      .then(() => setCopied(true))
  }

  const { mutate: generateNewWebhookSigningSecret, isPending: generating } = useMutation({
    mutationFn: () => $fetch('/user/newWebhookSecret', {
      method: 'POST',
    }),
    onSuccess: (data) => {
      refetchWebhookSigningSecret()
      setHiddenWebhookSigningSecret(data?.webhook_signing_secret)
      setHidden(false)
    },
    onError: (error) => {
      console.error('Error generating new webhook signing secret:', error)
      toast.error('Error generating new webhook signing secret')
    },
  })

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col">
        <div style={{ marginBottom: 17 }}>
          <p style={{ fontSize: 14, fontWeight: 400 }}>
            Webhook Signing Secret
          </p>
          <p style={{ color: 'GrayText', fontSize: 12 }}>
            Never share this publicly. Warning: Once generated, the secret will
            only be viewable once and cannot be retrieved later without
            replacing it.
          </p>
        </div>
        {isLoadingWebhookSigningSecret
          ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading Webhook Signing Secret...</span>
              </div>
            )
          : !hasActiveWebhook
              ? (
                  <Alert
                    color="primary"
                    description={(
                      <span>
                        Generate a new webhook signing secret to verify webhooks. For more information, see the
                        {' '}
                        <a
                          href="https://docs.bland.ai/tutorials/webhook-signing"
                          target="_blank"
                          className={`
                            underline
                            hover:text-blue-500
                          `}
                        >
                          documentation.
                        </a>
                      </span>
                    )}
                    endContent={(
                      <HeroButton
                        color="primary"
                        size="sm"
                        variant="flat"
                        radius="sm"
                        onPress={generateNewWebhookSigningSecret}
                        className="mt-1"
                        isLoading={generating}
                      >
                        Generate
                      </HeroButton>
                    )}
                    title="Webhook Signing Secret"
                    variant="faded"
                  />
                )
              : (
                  <>
                    <div className={`
                      rounded- flex items-center justify-between border
                      border-gray-200 bg-gray-50 p-2
                    `}
                    >
                      <div className="cursor-default text-black opacity-50">
                        {hidden ? HIDDEN_SECRET : hiddenWebhookSigningSecret}
                      </div>
                      <div className="flex gap-x-1.5 text-black opacity-60">
                        {hiddenWebhookSigningSecret && (
                          <>
                            <button
                              type="button"
                              onClick={handleRevealClick}
                              className="p-0.5"
                            >
                              {isLoadingWebhookSigningSecret ? undefined : hidden ? <FaEye /> : <FaEyeSlash />}
                            </button>
                            <button
                              type="button"
                              onClick={handleCopyClick}
                              className="p-0.5"
                            >
                              {isLoadingWebhookSigningSecret ? undefined : copied ? <FaCheck /> : <FaCopy />}
                            </button>
                          </>
                        )}
                        {isLoadingWebhookSigningSecret && (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                      }}
                    >
                      <Button
                        type="button"
                        style={{ marginLeft: 'auto', cursor: 'pointer' }}
                        loading={generating}
                        onClick={generateNewWebhookSigningSecret}
                        color="iris"
                        variant="soft"
                      >
                        Replace Existing Secret
                      </Button>
                    </div>

                  </>
                )}
      </div>
    </div>
  )
}
